// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  customer/src/pages/policies/PolicyLayout.jsx
//  Shared layout for all policy pages
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

// ── Shared wrapper ────────────────────────────────
export function PolicyPage({ title, subtitle, children }) {
  const navigate = useNavigate();
  return (
    <div style={{ background: "#f8f8f8", minHeight: "100vh", fontFamily: "'Segoe UI', sans-serif" }}>
      {/* Header */}
      <div style={{
        background: "linear-gradient(135deg, #ff6b35, #f7931e)",
        padding: "0 0 28px",
        position: "sticky", top: 0, zIndex: 10,
      }}>
        {/* Back button bar */}
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "14px 16px 0",
        }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: "rgba(255,255,255,0.2)", border: "none",
              borderRadius: "50%", width: 36, height: 36,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <ArrowLeft size={18} color="#fff" />
          </button>
          <span style={{ color: "rgba(255,255,255,0.85)", fontSize: 13, fontWeight: 500 }}>
            Back
          </span>
        </div>
        {/* Title */}
        <div style={{ textAlign: "center", padding: "12px 20px 0" }}>
          <h1 style={{ color: "#fff", fontSize: "1.5rem", fontWeight: 800, margin: 0 }}>{title}</h1>
          <p style={{ color: "rgba(255,255,255,0.8)", fontSize: 13, marginTop: 6 }}>
            {subtitle || "Quickry — Akola's Delivery Platform"}
          </p>
        </div>
      </div>

      {/* Content */}
      <div style={{
        maxWidth: 680, margin: "0 auto",
        background: "#fff",
        borderRadius: "20px 20px 0 0",
        marginTop: -12,
        padding: "28px 20px 60px",
        boxShadow: "0 -4px 20px rgba(0,0,0,0.06)",
      }}>
        {/* Last updated badge */}
        <div style={{
          display: "inline-block",
          background: "#fff5f0", color: "#ff6b35",
          padding: "4px 14px", borderRadius: 20,
          fontSize: 11, fontWeight: 700, marginBottom: 24,
        }}>
          Last Updated: April 2025
        </div>
        {children}
        {/* Footer */}
        <div style={{
          textAlign: "center", marginTop: 32,
          paddingTop: 20, borderTop: "1px solid #f0f0f0",
          fontSize: 12, color: "#bbb",
        }}>
          © 2025 Quickry Technologies. All rights reserved.
        </div>
      </div>
    </div>
  );
}

// ── Section heading ───────────────────────────────
export function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <h2 style={{
        color: "#ff6b35", fontSize: "0.95rem", fontWeight: 700,
        marginBottom: 10, paddingBottom: 6,
        borderBottom: "2px solid #fff5f0",
      }}>
        {title}
      </h2>
      {children}
    </div>
  );
}

// ── Paragraph ─────────────────────────────────────
export function Para({ children }) {
  return (
    <p style={{ fontSize: "0.88rem", color: "#555", lineHeight: 1.7, marginBottom: 8 }}>
      {children}
    </p>
  );
}

// ── Bullet list ───────────────────────────────────
export function List({ items }) {
  return (
    <ul style={{ margin: "6px 0 8px 18px" }}>
      {items.map((item, i) => (
        <li key={i} style={{ fontSize: "0.88rem", color: "#555", lineHeight: 1.7, marginBottom: 5 }}>
          {item}
        </li>
      ))}
    </ul>
  );
}

// ── Info card ─────────────────────────────────────
export function InfoCard({ color = "orange", children }) {
  const colors = {
    orange: { bg: "#fff5f0", border: "#fed7aa" },
    green:  { bg: "#f0fdf4", border: "#bbf7d0" },
    red:    { bg: "#fef2f2", border: "#fecaca" },
    blue:   { bg: "#eff6ff", border: "#bfdbfe" },
    yellow: { bg: "#fefce8", border: "#fde68a" },
  };
  const c = colors[color] || colors.orange;
  return (
    <div style={{
      background: c.bg, border: `1px solid ${c.border}`,
      borderRadius: 12, padding: "14px 16px", marginBottom: 14,
    }}>
      {children}
    </div>
  );
}

// ── Contact section (shared across all pages) ─────
export function ContactSection() {
  return (
    <Section title="Contact Us">
      <Para>For any questions, please contact us at:</Para>
      <InfoCard color="orange">
        <div style={{ fontSize: "0.88rem", color: "#555", lineHeight: 2 }}>
          📧 <strong>Email:</strong> support@quickry.in<br />
          📍 <strong>Address:</strong> Akola, Maharashtra, India
        </div>
      </InfoCard>
    </Section>
  );
}

// ── Simple table ──────────────────────────────────
export function PolicyTable({ headers, rows }) {
  return (
    <div style={{ overflowX: "auto", marginBottom: 14 }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} style={{
                background: "#ff6b35", color: "#fff",
                padding: "10px 14px", fontSize: "0.8rem",
                textAlign: "left", fontWeight: 700,
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j} style={{
                  padding: "10px 14px", fontSize: "0.84rem",
                  color: "#444", borderBottom: "1px solid #f0f0f0",
                  background: i % 2 === 1 ? "#fafafa" : "#fff",
                }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}